export const LOGIN_PAGE_THEME_COUNT = 9

export type LoginPageDesign = {
  themeId: number
  imageUrl: string | null
  accentColor: string | null
}

export type LoginPageTheme = {
  id: number
  name: string
  description: string
  usesImage: boolean
  defaultImage: string | null
}

export const LOGIN_PAGE_THEMES: LoginPageTheme[] = [
  {
    id: 0,
    name: "Full-bleed hero",
    description: "Full-screen photo with only the login form.",
    usesImage: true,
    defaultImage:
      "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    id: 1,
    name: "Split welcome",
    description: "Headline on the left, login card on the right.",
    usesImage: false,
    defaultImage: null,
  },
  {
    id: 2,
    name: "Insights panel",
    description: "Login on the left with a colored insights panel.",
    usesImage: false,
    defaultImage: null,
  },
  {
    id: 3,
    name: "Gradient split",
    description: "Gradient hero beside a clean login card.",
    usesImage: false,
    defaultImage: null,
  },
  {
    id: 4,
    name: "Centered stack",
    description: "Centered heading with the login card below.",
    usesImage: false,
    defaultImage: null,
  },
  {
    id: 5,
    name: "Soft workspace",
    description: "Soft card layout with space for a custom photo.",
    usesImage: false,
    defaultImage: null,
  },
  {
    id: 6,
    name: "Scenic split",
    description: "Photo panel on the left, login card on the right.",
    usesImage: true,
    defaultImage:
      "https://images.pexels.com/photos/3184406/pexels-photo-3184406.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    id: 7,
    name: "Calm wellbeing",
    description: "Login on the left with a calm accent panel.",
    usesImage: false,
    defaultImage: null,
  },
  {
    id: 8,
    name: "Focused card",
    description: "Single centered login card on a light backdrop.",
    usesImage: false,
    defaultImage: null,
  },
]

export function normalizeLoginThemeId(value: unknown): number {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 0 || parsed >= LOGIN_PAGE_THEME_COUNT) return 0
  return parsed
}

export function normalizeLoginPageDesign(input: unknown): LoginPageDesign {
  const raw = input && typeof input === "object" && !Array.isArray(input) ? (input as Record<string, unknown>) : {}
  const imageUrl = String(raw.imageUrl || raw.image_url || "").trim()
  const accentColor = String(raw.accentColor || raw.accent_color || "").trim()
  return {
    themeId: normalizeLoginThemeId(raw.themeId ?? raw.theme_id),
    imageUrl: imageUrl || null,
    accentColor: /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(accentColor) ? accentColor : null,
  }
}

export function resolveLoginThemeImage(design: LoginPageDesign): string | null {
  if (design.imageUrl) return design.imageUrl
  return LOGIN_PAGE_THEMES[design.themeId]?.defaultImage || null
}

export function getLoginTheme(themeId: number): LoginPageTheme {
  return LOGIN_PAGE_THEMES[normalizeLoginThemeId(themeId)]
}

export function fileToCompressedDataUrl(file: File): Promise<{ dataUrl: string; accentColor: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error("Could not read the image."))
    reader.onload = () => {
      const image = new Image()
      image.onerror = () => reject(new Error("Could not load the image."))
      image.onload = () => {
        const maxWidth = 1600
        const scale = Math.min(1, maxWidth / Math.max(image.width, 1))
        const width = Math.max(1, Math.round(image.width * scale))
        const height = Math.max(1, Math.round(image.height * scale))
        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        if (!ctx) {
          reject(new Error("Could not process the image."))
          return
        }
        ctx.drawImage(image, 0, 0, width, height)
        const sample = ctx.getImageData(0, 0, Math.min(40, width), Math.min(40, height)).data
        let r = 0
        let g = 0
        let b = 0
        let count = 0
        for (let i = 0; i < sample.length; i += 4) {
          r += sample[i]
          g += sample[i + 1]
          b += sample[i + 2]
          count += 1
        }
        const toHex = (value: number) =>
          Math.max(0, Math.min(255, Math.round(value / Math.max(count, 1))))
            .toString(16)
            .padStart(2, "0")
        resolve({
          dataUrl: canvas.toDataURL("image/jpeg", 0.82),
          accentColor: `#${toHex(r)}${toHex(g)}${toHex(b)}`,
        })
      }
      image.src = String(reader.result || "")
    }
    reader.readAsDataURL(file)
  })
}
